import { CheckCircle2, ChevronDown, Circle, Loader2, StopCircle } from "lucide-react";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { ChatMessageClipboard } from "src/components/ui/chat/ChatMessageClipboard";
import { MessageWithToolReferences } from "src/components/ui/chat/message-with-tool-references";
import { ToolCallSheet } from "src/components/ui/chat/tool-calls/ToolCallSheet";
import { V2AgentThoughtProcessRenderer } from "src/components/ui/chat/tool-calls/V2AgentThoughtProcessRenderer";
import { isFilesystemTool } from "src/components/ui/chat/tool-calls/v2Utils";
import { Markdown } from "src/components/ui/Markdown";
import { SectionProgressType } from "src/pages/DigestPage/DigestDetails";

import {
  DigestSectionAccordion_jobRun$data,
  DigestSectionAccordion_jobRun$key,
} from "./__generated__/DigestSectionAccordion_jobRun.graphql";

export const digestSectionAccordionFragment = graphql`
  fragment DigestSectionAccordion_jobRun on GraphQLJobRun {
    digestSections {
      section
      text
      isComplete
      message {
        ...messageWithToolReferences_message
        content
        startedAt
        endedAt
        agentThoughtProcess {
          text
          thinking
          thinkingSummary
          subagentDescription
          subagentOutput
          subagentItems {
            text
            thinking
            thinkingSummary
            toolUse {
              toolUse {
                id
                name
              }
              ...V2AgentThoughtProcessRenderer_toolCall
              ...ChecklistToolCallResult_toolCall
              ...ToolCallSheet_toolCall
              ...SkillCluster_toolCall
            }
          }
          toolUse {
            toolUse {
              id
              name
            }
            ...V2AgentThoughtProcessRenderer_toolCall
            ...ChecklistToolCallResult_toolCall
            ...ToolCallSheet_toolCall
            ...SkillCluster_toolCall
          }
        }
      }
    }
  }
`;

type SectionStatusType = "completed" | "running" | "pending" | "canceled";

type ThoughtProcessItems = NonNullable<
  NonNullable<
    DigestSectionAccordion_jobRun$data["digestSections"][number]["message"]
  >["agentThoughtProcess"]
>;

/**
 * Keep only the last TodoWrite/UpdateTodoTool item so the user sees
 * one up-to-date checklist instead of repeated snapshots.
 */
function deduplicateTodos(items: ThoughtProcessItems): ThoughtProcessItems {
  const isTodoItem = (item: ThoughtProcessItems[number]) => {
    const name = item.toolUse?.toolUse?.name;
    return name === "TodoWrite" || name === "UpdateTodoTool";
  };

  let lastTodoIdx = -1;
  for (let i = items.length - 1; i >= 0; i--) {
    if (isTodoItem(items[i])) {
      lastTodoIdx = i;
      break;
    }
  }
  if (lastTodoIdx === -1) return items;

  return items.filter((item, idx) => !isTodoItem(item) || idx === lastTodoIdx);
}

interface InputSectionType {
  section: string;
}

interface DigestSectionAccordionProps {
  jobRun: DigestSectionAccordion_jobRun$key;
  isInProgress: boolean;
  isStopped: boolean;
  sectionProgress: SectionProgressType | null;
  inputSections: InputSectionType[];
}

type DigestSection = DigestSectionAccordion_jobRun$data["digestSections"][number];

interface DigestSectionThoughtProcessProps {
  agentThoughtProcess: NonNullable<DigestSection["message"]>["agentThoughtProcess"];
  startedAt: string;
  endedAt: string | null | undefined;
  isThinking: boolean;
}

const StartingAgentIndicator = React.memo(function StartingAgentIndicator() {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
      <Loader2 className="w-4 h-4 animate-spin" />
      <span>Starting agent...</span>
    </div>
  );
});

const DigestSectionThoughtProcess = React.memo<DigestSectionThoughtProcessProps>(
  function DigestSectionThoughtProcess({ agentThoughtProcess, startedAt, endedAt, isThinking }) {
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [activeToolIndex, setActiveToolIndex] = useState(0);

    const toolUses = useMemo(() => {
      return (agentThoughtProcess || [])
        .map((item, index) => ({ toolUse: item.toolUse, originalIndex: index }))
        .filter((item) => {
          if (!item.toolUse) return false;
          const toolName = item.toolUse.toolUse?.name;
          if (!toolName) return false;
          if (toolName === "UpdateTodoTool" || toolName === "TodoWrite") return false;
          return !isFilesystemTool(toolName);
        });
    }, [agentThoughtProcess]);

    const handleToolClick = useCallback(
      (index: number) => {
        const toolIndex = toolUses.findIndex((item) => item.originalIndex === index);
        if (toolIndex !== -1) {
          setActiveToolIndex(toolIndex);
          setIsSheetOpen(true);
        }
      },
      [toolUses]
    );

    const moveToNextTool = useCallback(() => {
      if (activeToolIndex < toolUses.length - 1) {
        setActiveToolIndex(activeToolIndex + 1);
      }
    }, [activeToolIndex, toolUses.length]);

    const moveToPrevTool = useCallback(() => {
      if (activeToolIndex > 0) {
        setActiveToolIndex(activeToolIndex - 1);
      }
    }, [activeToolIndex]);

    const activeTool = toolUses[activeToolIndex]?.toolUse;

    return (
      <>
        <V2AgentThoughtProcessRenderer
          items={agentThoughtProcess}
          isThinking={isThinking}
          startedAt={startedAt}
          endedAt={endedAt}
          onToolClick={handleToolClick}
        />
        {isSheetOpen && activeTool && (
          <ToolCallSheet
            toolCall={activeTool}
            index={activeToolIndex}
            totalCount={toolUses.length}
            isOpen={isSheetOpen}
            onOpenChange={setIsSheetOpen}
            onNext={moveToNextTool}
            onPrevious={moveToPrevTool}
          />
        )}
      </>
    );
  }
);

function SectionStatusTypeIcon({ status }: { status: SectionStatusType }) {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />;
    case "running":
      return <Loader2 className="w-4 h-4 text-primary animate-spin shrink-0" />;
    case "pending":
      return <Circle className="w-4 h-4 text-muted-foreground/40 shrink-0" />;
    case "canceled":
      return <StopCircle className="w-4 h-4 text-red-500 shrink-0" />;
  }
}

function SectionStatusTypeLabel({ status }: { status: SectionStatusType }) {
  switch (status) {
    case "completed":
      return <span className="text-xs text-green-500 font-medium">Done</span>;
    case "running":
      return <span className="text-xs text-primary font-medium">Running</span>;
    case "pending":
      return <span className="text-xs text-muted-foreground/40 font-medium">Pending</span>;
    case "canceled":
      return <span className="text-xs text-red-500 font-medium">Canceled</span>;
  }
}

export default function DigestSectionAccordion({
  jobRun,
  isInProgress,
  isStopped,
  sectionProgress,
  inputSections,
}: DigestSectionAccordionProps) {
  const data = useFragment(digestSectionAccordionFragment, jobRun);
  const isLiveProgress = isInProgress && inputSections.length > 0;

  const sections = useMemo(() => {
    const digestSectionMap = new Map<
      string,
      DigestSectionAccordion_jobRun$data["digestSections"][number]
    >();
    for (const ds of data.digestSections) {
      if (ds.section) {
        digestSectionMap.set(ds.section, ds);
      }
    }

    if (!isLiveProgress) {
      if (isStopped && inputSections.length > 0) {
        return inputSections.map((inputSection, index) => {
          const digestSection = digestSectionMap.get(inputSection.section);
          let status: SectionStatusType;
          if (!digestSection) {
            status = "pending";
          } else if (!digestSection.isComplete) {
            status = "canceled";
          } else {
            status = "completed";
          }
          return {
            id: `section-${index}`,
            title: inputSection.section || `Section ${index + 1}`,
            message: digestSection?.message ?? null,
            text: digestSection?.text ?? null,
            status,
          };
        });
      }

      return data.digestSections.map((section, index) => ({
        id: `section-${index}`,
        title: section.section || `Section ${index + 1}`,
        message: section.message,
        text: section.text,
        status: (isStopped && !section.isComplete ? "canceled" : "completed") as SectionStatusType,
      }));
    }

    const completedCount = data.digestSections.filter((ds) => ds.isComplete).length;
    const activeSectionIndex = sectionProgress?.activeSectionIndex ?? completedCount;

    return inputSections.map((inputSection, index) => {
      const digestSection = digestSectionMap.get(inputSection.section);
      let status: SectionStatusType;
      if (index < activeSectionIndex) {
        status = "completed";
      } else if (index === activeSectionIndex) {
        status = "running";
      } else {
        status = "pending";
      }

      const hasContent = digestSection?.message != null;

      return {
        id: `section-${index}`,
        title: inputSection.section || `Section ${index + 1}`,
        message: hasContent ? digestSection!.message : null,
        text: digestSection?.text ?? null,
        status,
      };
    });
  }, [isLiveProgress, isStopped, data.digestSections, inputSections, sectionProgress]);

  const [openSections, setOpenSections] = useState<string[]>(() =>
    sections
      .filter((s) => s.status !== "pending" && !(s.status === "canceled" && !s.message && !s.text))
      .map((s) => s.id)
  );

  // Auto-expand running section when it changes
  React.useEffect(() => {
    if (!isLiveProgress) return;
    const runningSection = sections.find((s) => s.status === "running");
    if (runningSection) {
      setOpenSections((prev) =>
        prev.includes(runningSection.id) ? prev : [...prev, runningSection.id]
      );
    }
  }, [isLiveProgress, sections]);

  const toggleSection = (sectionId: string) => {
    setOpenSections((prev) => {
      if (prev.includes(sectionId)) {
        return prev.filter((id) => id !== sectionId);
      } else {
        return [...prev, sectionId];
      }
    });
  };

  if (sections.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {sections.map((section, index) => {
        const isPending = section.status === "pending";
        const isRunning = section.status === "running";
        const isOpen = openSections.includes(section.id);

        return (
          <DigestSectionCard
            key={section.id}
            section={section}
            index={index}
            isPending={isPending}
            isRunning={isRunning}
            isOpen={isOpen}
            onToggle={toggleSection}
          />
        );
      })}
    </div>
  );
}

interface SectionType {
  id: string;
  title: string;
  message: DigestSectionAccordion_jobRun$data["digestSections"][number]["message"] | null;
  text: string | null | undefined;
  status: SectionStatusType;
}

const DigestSectionCard = React.memo<{
  section: SectionType;
  index: number;
  isPending: boolean;
  isRunning: boolean;
  isOpen: boolean;
  onToggle: (id: string) => void;
}>(function DigestSectionCard({ section, index, isPending, isRunning, isOpen, onToggle }) {
  const dedupedThoughtProcess = useMemo(() => {
    if (!isRunning || !section.message?.agentThoughtProcess?.length) return null;
    return deduplicateTodos(section.message.agentThoughtProcess);
  }, [isRunning, section.message?.agentThoughtProcess]);

  return (
    <div
      className={`bg-card/50 border border-border/50 rounded-lg overflow-hidden transition-all duration-200 ${
        isPending ? "opacity-50" : ""
      }`}
    >
      <div
        className={`px-5 py-3 flex items-center justify-between transition-all duration-200 ${
          isPending
            ? "cursor-default"
            : "cursor-pointer hover:bg-accent/50"
        }`}
        onClick={() => !isPending && onToggle(section.id)}
      >
        <div className="flex items-center gap-2">
          <SectionStatusTypeIcon status={section.status} />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Section {index + 1}:
          </span>
          <span
            className={`text-sm font-medium ${
              isPending ? "text-muted-foreground/40" : "text-foreground"
            }`}
          >
            {section.title}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <SectionStatusTypeLabel status={section.status} />
          {!isPending && (
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          )}
        </div>
      </div>
      {!isPending && (
        <div
          className={`overflow-hidden transition-all duration-200 ${
            isOpen ? "opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="px-5 py-4 border-t border-border/50">
            {section.message ? (
              <div className="space-y-3">
                {section.message.agentThoughtProcess &&
                section.message.agentThoughtProcess.length > 0 ? (
                  <DigestSectionThoughtProcess
                    agentThoughtProcess={
                      dedupedThoughtProcess ?? section.message.agentThoughtProcess
                    }
                    startedAt={section.message.startedAt}
                    endedAt={section.message.endedAt}
                    isThinking={isRunning}
                  />
                ) : isRunning ? (
                  <StartingAgentIndicator />
                ) : null}
                {!isRunning && (
                  <MessageWithToolReferences
                    message={section.message}
                    copyButton={
                      <ChatMessageClipboard
                        messageContent={section.message.content || ""}
                      />
                    }
                  />
                )}
              </div>
            ) : isRunning ? (
              <StartingAgentIndicator />
            ) : section.text ? (
              <div>
                <Markdown
                  className="text-foreground"
                  copyButton={
                    <ChatMessageClipboard
                      messageContent={section.text}
                    />
                  }
                >
                  {section.text}
                </Markdown>
              </div>
            ) : (
              <div className="text-muted-foreground italic">
                No message data available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});
